// Unacknowledged sensor-alert inbox. Parallel to AlertsInbox (satellite) +
// WeatherAlertsInbox. Renders nothing when empty to stay out of the way.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SensorAlert } from '@greeniq/shared';
import { acknowledgeSensorAlert, getSensorAlerts } from '../api/sensors.js';
import { DataCard } from './ui/data-card';
import { Badge, type BadgeProps } from './ui/badge';
import { AlertRow } from './AlertRow';

interface Props {
  courseSlug: string;
}

const SEVERITY_VARIANT: Record<SensorAlert['severity'], BadgeProps['variant']> = {
  info: 'secondary',
  warn: 'warning',
  critical: 'destructive',
};

const SEVERITY_LABEL: Record<SensorAlert['severity'], string> = {
  info: 'Info',
  warn: 'Warn',
  critical: 'Critical',
};

export function SensorAlertsInbox({ courseSlug }: Props) {
  const [alerts, setAlerts] = useState<SensorAlert[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    try {
      const resp = await getSensorAlerts(courseSlug, 'unacknowledged', 10);
      setAlerts(resp.alerts);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  async function onAck(alertId: number) {
    setAcking(alertId);
    try {
      await acknowledgeSensorAlert(courseSlug, alertId);
      setAlerts((prev) => (prev ?? []).filter((a) => a.id !== alertId));
    } finally {
      setAcking(null);
    }
  }

  if (loading) return null;
  if (!alerts || alerts.length === 0) return null;

  return (
    <DataCard
      className="mt-6"
      title="Sensor alerts"
      actions={<Badge variant="warning">{alerts.length} unacknowledged</Badge>}
    >
      <ul>
        {alerts.map((a) => (
          <AlertRow
            key={a.id}
            badge={SEVERITY_LABEL[a.severity]}
            badgeVariant={SEVERITY_VARIANT[a.severity]}
            title={describeAlert(a)}
            meta={
              <>
                Zone #{a.zoneId} · {a.forDate} ·{' '}
                <Link
                  to={`/app/${courseSlug}/zones/${a.zoneId}`}
                  className="text-greeniq-400 hover:text-greeniq-300"
                >
                  open zone →
                </Link>
              </>
            }
            onAcknowledge={() => onAck(a.id)}
            acknowledging={acking === a.id}
          />
        ))}
      </ul>
    </DataCard>
  );
}

function describeAlert(a: SensorAlert): string {
  const vwc = Number(a.observedValue).toFixed(3);
  const thr = Number(a.thresholdValue).toFixed(3);
  switch (a.alertType) {
    case 'green_critical_vwc':
      return `Green VWC critically low: ${vwc} (threshold ${thr})`;
    case 'green_low_vwc':
      return `Green VWC low: ${vwc} (threshold ${thr})`;
    case 'fairway_critical_vwc':
      return `Fairway VWC critically low: ${vwc} (threshold ${thr})`;
    case 'fairway_low_vwc':
      return `Fairway VWC low: ${vwc} (threshold ${thr})`;
    default:
      return `${a.alertType}: ${a.metric} ${vwc} < ${thr}`;
  }
}
